import React, { useRef, useState } from "react";
import axios from "axios";
import { useTheme } from "../../middleware/ThemeContext";
import styled from "styled-components";
import EditIcon from "../../assets/icons/EditIcon";
import { useToast } from "../toast/ToastContext";
import CrossIcon from "../../assets/icons/CrossIcon";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import TickIcon from "../../assets/icons/TickIcon";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import { LoadingSpinner } from "../../features/ui/LoadingSpinner";
import { Theme } from "../../utils/themes";
import FillEditIcon from "../../assets/icons/FillEditIcon";
import { getInitial } from "../../utils/helper";

interface AvatarProps {
  name: string;
  imageUrl?: string;
  size?: number;
  rounded?: boolean;
  className?: string;
  border?: string;
  enableUpload?: boolean; // 👈 make upload optional
  onUploadSuccess?: (url: string) => void; // callback when uploaded
  onUpload?: (file: File) => Promise<{ key: string }>; // 👈 pass file -> return uploaded URL
  handleUpdate?: () => void;
  loading?: boolean
}

interface UploadedFile {
  file: File;
  name: string;
  type: string;
  rename: string;
  progress: number;
}


const Avatar: React.FC<AvatarProps> = ({
  name,
  imageUrl,
  size = 40,
  rounded = true,
  className = "",
  border = "none",
  enableUpload = false,
  onUpload,
  handleUpdate,
  loading
}) => {

  const [localImage, setLocalImage] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { theme } = useTheme();
  const initial = getInitial(name);
  const [hovered, setHovered] = useState(false);
  const handleFileSelect = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);

    setLocalImage(localUrl);
    if (!file || !onUpload) return;

    try {
      setUploading(true);
      // 🔥 call parent-provided upload handler
      await onUpload(file);

    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setLocalImage(null); // preview clear karo

    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // file input reset karo
    }
  };

  const handleSave = () => {
    if (handleUpdate) {
      handleUpdate(); // 👈 call parent-provided update handler
      setLocalImage(null);
    }
  };

  return (
    <div
      className={`d-flex align-items-center justify-content-center bg-white text-primary fw-bold position-relative cusror-pointer ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: rounded ? "50%" : "0.25rem",
        // overflow: "hidden",
        fontSize: size / 2,
        textTransform: "uppercase",
        border: border,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {imageUrl || localImage ? (
        <img
          src={localImage || imageUrl}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
        />
      ) : (
        <span>{initial}</span>
      )}

      {/* {enableUpload && (
        <>
          {loading ? <StyledDiv>
            <LoadingSpinner size="20px" color={theme.CorporateTheme.colors.secondary} />
          </StyledDiv> : <StyledDiv>
            {localImage ? <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <div>
                <DeleteIcon
                  color={theme.CorporateTheme.colors.secondary}
                  size={24}
                  onClick={handleRemoveImage}
                />
              </div>
              <div>
                <TickIcon
                  color={theme.CorporateTheme.colors.secondary}
                  size={24}
                  onClick={handleSave}
                />
              </div>
            </div> : <EditIcon onClick={handleFileSelect} color={theme.CorporateTheme.colors.secondary} size={24} />}
          </StyledDiv>}

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </>
      )} */}
      {enableUpload && (
        <>
          <StyledDiv
            theme={theme}
            localImage={localImage}
            size={size}
            style={{
              opacity: hovered || localImage ? 1 : 0, // show on hover or if preview exists
              transition: "opacity 0.2s",
            }}
          >
            {loading ? (
              <LoadingSpinner size="20px" color="black" />
            ) : localImage ? (
              <>
                <DeleteIcon
                  color="black"
                  size={20}
                  onClick={handleRemoveImage}
                  className="cursor-pointer"
                />
                <TickIcon
                  color="black"
                  size={20}
                  onClick={handleSave}
                  className="cursor-pointer"

                />
              </>
            ) : (
              <FillEditIcon
                onClick={handleFileSelect}
                color="black"
                size={24}
                className="cursor-pointer"
              />
            )}
          </StyledDiv>

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </>
      )}
    </div>
  );
};



const hexToRgb = (hex: string) => {
  const sanitizedHex = hex.replace('#', '');
  const bigint = parseInt(sanitizedHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

const StyledDiv = styled.div<{ localImage: boolean; theme: Theme; size: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ localImage }) => (localImage ? '8px' : '4px')};
  
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
      background: ${({ theme }) => `rgba(${hexToRgb(theme.CreateFormTheme.colors.inputBorders)}, 0.7)`}; 

  border-radius:50%;
  
  z-index: 999;
  opacity: 0;
  transition: opacity 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;







export default Avatar;
